import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "../../theming";
import { useResponsive } from "../../contexts/ResponsiveContext";

const DesignPreferencesSection = ({ data, isEditing, onSave }) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [editedData, setEditedData] = useState(
    data || {
      style: "",
      tone: "",
      keyElements: [],
      colorScheme: "",
      imageryStyle: "",
    }
  );

  useEffect(() => {
    if (data) {
      setEditedData(data);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeyElementChange = (index, value) => {
    const newKeyElements = [...editedData.keyElements];
    newKeyElements[index] = value;
    handleChange("keyElements", newKeyElements);
  };

  const addKeyElement = () => {
    handleChange("keyElements", [...editedData.keyElements, ""]);
  };

  const removeKeyElement = (index) => {
    const newKeyElements = editedData.keyElements.filter((_, i) => i !== index);
    handleChange("keyElements", newKeyElements);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              color: theme.primaryColor,
              mb: 2,
            }}
          >
            Design Preferences
          </Typography>
        </Grid> */}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Style"
            value={editedData.style}
            onChange={(e) => handleChange("style", e.target.value)}
            disabled={!isEditing}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${theme.primaryColor}40`,
                },
                "&:hover fieldset": {
                  borderColor: theme.primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.primaryColor,
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.textColor,
              },
              "& .MuiInputBase-input": {
                color: theme.textColor,
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Tone"
            value={editedData.tone}
            onChange={(e) => handleChange("tone", e.target.value)}
            disabled={!isEditing}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${theme.primaryColor}40`,
                },
                "&:hover fieldset": {
                  borderColor: theme.primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.primaryColor,
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.textColor,
              },
              "& .MuiInputBase-input": {
                color: theme.textColor,
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.textColor,
              mb: 1,
            }}
          >
            Key Elements
          </Typography>
          {isEditing ? (
            <Box>
              {editedData.keyElements.map((element, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    value={element}
                    onChange={(e) =>
                      handleKeyElementChange(index, e.target.value)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: `${theme.primaryColor}40`,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.primaryColor,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.primaryColor,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: theme.textColor,
                      },
                      "& .MuiInputBase-input": {
                        color: theme.textColor,
                      },
                    }}
                  />
                  <Button
                    onClick={() => removeKeyElement(index)}
                    sx={{
                      color: theme.accentColor,
                      "&:hover": {
                        backgroundColor: `${theme.accentColor}20`,
                      },
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button
                onClick={addKeyElement}
                sx={{
                  color: theme.primaryColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}20`,
                  },
                }}
              >
                Add Element
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {editedData.keyElements.map((element, index) => (
                <Chip
                  key={index}
                  label={element}
                  sx={{
                    backgroundColor: `${theme.primaryColor}20`,
                    color: theme.textColor,
                  }}
                />
              ))}
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: theme.textColor }}>
              Color Scheme
            </InputLabel>
            <Select
              value={editedData.colorScheme}
              onChange={(e) => handleChange("colorScheme", e.target.value)}
              disabled={!isEditing}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${theme.primaryColor}40`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.primaryColor,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.primaryColor,
                },
                color: theme.textColor,
              }}
            >
              <MenuItem value="Modern">Modern</MenuItem>
              <MenuItem value="Classic">Classic</MenuItem>
              <MenuItem value="Minimalist">Minimalist</MenuItem>
              <MenuItem value="Bold">Bold</MenuItem>
              <MenuItem value="To be determined">To be determined</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Imagery Style"
            value={editedData.imageryStyle}
            onChange={(e) => handleChange("imageryStyle", e.target.value)}
            disabled={!isEditing}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${theme.primaryColor}40`,
                },
                "&:hover fieldset": {
                  borderColor: theme.primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.primaryColor,
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.textColor,
              },
              "& .MuiInputBase-input": {
                color: theme.textColor,
              },
            }}
          />
        </Grid>

        {isEditing && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: theme.primaryColor,
                  color: theme.buttonTextColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}CC`,
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DesignPreferencesSection;
